import { z } from 'zod';
import type { FlowScreen, QuizData } from '../types';

/**
 * DEV-1135 flow-as-data schema — the single validation contract for templated
 * quiz flows, used at BUILD time (Lambda / local build validates the baked
 * quiz.json) and at RUNTIME (prod bundles fetch quiz config from the API on
 * every load, so a DDB edit can deliver a flow this bundle can't render; see
 * store.initializeQuizData → resolveFlowConfig for the fallback posture).
 *
 * Rung-1 invariants (build-failing / runtime-fallback):
 *   - flow is a non-empty ordered list (linear only; screens carry stable ids
 *     so a `next` branching field can be added additively later)
 *   - exactly one gate screen
 *   - exactly one redirect screen and it is the LAST screen
 *   - screen ids unique
 *   - every question screen references an existing question id
 *   - every url_logic rule's answer values are answerable from the flow's
 *     questions (else redirects silently fall back to quiz_default_url — the
 *     url_logic coupling trap from PR #39 / TODOS.md)
 */

const questionScreenSchema = z.object({
  id: z.string().min(1),
  type: z.literal('question'),
  question_id: z.string().min(1),
});

const gateScreenSchema = z.object({
  id: z.string().min(1),
  type: z.literal('gate'),
});

const questionGateScreenSchema = z.object({
  id: z.string().min(1),
  type: z.literal('question-gate'),
  question_id: z.string().min(1),
});

const formGateScreenSchema = z.object({
  id: z.string().min(1),
  type: z.literal('form-gate'),
  question_ids: z.array(z.string().min(1)).min(1),
});

const redirectScreenSchema = z.object({
  id: z.string().min(1),
  type: z.literal('redirect'),
});

export const flowScreenSchema = z.discriminatedUnion('type', [
  questionScreenSchema,
  gateScreenSchema,
  questionGateScreenSchema,
  formGateScreenSchema,
  redirectScreenSchema,
]);

export const flowSchema = z.array(flowScreenSchema).min(1);

export interface FlowValidationResult {
  ok: boolean;
  errors: string[];
  screens: FlowScreen[];
}

/**
 * Validate a quiz config's flow against the schema AND the cross-field
 * invariants that zod alone can't express. Returns all errors (not just the
 * first) so authoring feedback is complete in one pass.
 */
export function validateFlowConfig(
  quizData: Pick<QuizData, 'flow' | 'questions' | 'url_logic'>
): FlowValidationResult {
  const errors: string[] = [];

  const parsed = flowSchema.safeParse(quizData.flow);
  if (!parsed.success) {
    return {
      ok: false,
      errors: parsed.error.issues.map(
        (issue) => `flow schema: ${issue.path.join('.') || '(root)'} ${issue.message}`
      ),
      screens: [],
    };
  }
  const screens = parsed.data as FlowScreen[];

  const ids = screens.map((screen) => screen.id);
  const dupIds = ids.filter((id, i) => ids.indexOf(id) !== i);
  if (dupIds.length > 0) {
    errors.push(`duplicate screen ids: ${[...new Set(dupIds)].join(', ')}`);
  }

  // 'question-gate' (combined single-page mechanic) satisfies the gate
  // requirement — the lead form lives on that screen.
  const gates = screens.filter(
    (screen) =>
      screen.type === 'gate' ||
      screen.type === 'question-gate' ||
      screen.type === 'form-gate'
  );
  if (gates.length !== 1) {
    errors.push(`flow must contain exactly one gate screen (found ${gates.length})`);
  }

  const redirects = screens.filter((screen) => screen.type === 'redirect');
  if (redirects.length !== 1) {
    errors.push(`flow must contain exactly one redirect screen (found ${redirects.length})`);
  } else if (screens[screens.length - 1].type !== 'redirect') {
    errors.push('the redirect screen must be the last screen in the flow');
  }

  const questionIds = new Set((quizData.questions || []).map((q) => q.id));
  const flowQuestionIds = new Set<string>();
  for (const screen of screens) {
    if (screen.type === 'question' || screen.type === 'question-gate') {
      flowQuestionIds.add(screen.question_id);
      if (!questionIds.has(screen.question_id)) {
        errors.push(`question screen "${screen.id}" references unknown question "${screen.question_id}"`);
      }
    } else if (screen.type === 'form-gate') {
      for (const qid of screen.question_ids) {
        flowQuestionIds.add(qid);
        if (!questionIds.has(qid)) {
          errors.push(`form-gate screen "${screen.id}" references unknown question "${qid}"`);
        }
      }
    }
  }

  // url_logic coupling: every answer value a rule matches on must be reachable
  // from a question that is actually IN the flow.
  const answerableValues = new Set<string>();
  for (const question of quizData.questions || []) {
    if (!flowQuestionIds.has(question.id)) continue;
    for (const answer of question.answers || []) {
      if (Array.isArray(answer)) answerableValues.add(answer[0]);
    }
  }
  for (const rule of quizData.url_logic || []) {
    for (const answer of rule.answers || []) {
      if (!answerableValues.has(answer.value)) {
        errors.push(
          `url_logic answer value "${answer.value}" is not answerable from any question in the flow`
        );
      }
    }
  }

  return { ok: errors.length === 0, errors, screens };
}
